<template>
  <div class="chart-balance">
    <v-card class="chartWrapper mx-auto rounded-lg">
      <div class="chartCombo">
        <div v-if="isOrder">
          <table>
            <tr>
              <td>
                <NormalButton
                  :text="$t('発注')"
                  class="mr-2"
                  btnClass="pa-2"
                  height="32px"
                  width="100px"
                  @click="changeChart(true)"
                />
              </td>
              <td>
                <NormalButton
                  :text="$t('注文')"
                  class="mr-2"
                  btnClass="pa-2"
                  height="32px"
                  width="100px"
                  color="white"
                  textColor="thinText--text"
                  @click="changeChart(false)"
                />
              </td>
            </tr>
          </table>
        </div>
        <div v-else>
          <table>
            <tr>
              <td>
                <NormalButton
                  :text="$t('発注')"
                  class="mr-2"
                  btnClass="pa-2"
                  height="32px"
                  width="100px"
                  color="white"
                  textColor="thinText--text"
                  @click="changeChart(true)"
                />
              </td>
              <td>
                <NormalButton
                  :text="$t('注文')"
                  class="mr-2"
                  btnClass="pa-2"
                  height="32px"
                  width="100px"
                  @click="changeChart(false)"
                />
              </td>
            </tr>
          </table>
        </div>
        <div class="chartInfo">
          <v-progress-circular
            v-if="isLoading"
            class="loading"
            indeterminate
            color="primary"
            :size="70"
            :width="7"
          ></v-progress-circular>
          <line-chart
            v-else
            :chartData="chartData"
            :options="options"
            :height="230"
          />
        </div>
        <div class="chartInfo">
          <v-progress-circular
            v-if="isLoading"
            class="loading"
            indeterminate
            color="primary"
            :size="70"
            :width="7"
          ></v-progress-circular>
          <line-chart
            v-else
            :chartData="quantityChartData"
            :options="optionsForQuantity"
            :height="230"
          />
        </div>
      </div>
      <div class="dataInfo">
        <div
          class="remarks"
          v-for="(item, index) in aggregateRemarkData"
          :key="index"
        >
          <div
            v-bind:class="'label color' + ((index + 1) % 10)"
            v-if="selectedIds.includes(item.productId)"
          >
            {{ item.productName }}
          </div>
        </div>
      </div>
    </v-card>
    <br />
    <div>
      <DataTable
        @table-check-event="setSelectedIds"
        :isOrder="isOrder"
        :aggregateTableData="aggregateTableData"
      />
    </div>
  </div>
</template>

<script>
import LineChart from './Chart/LineChart.js'
import DataTable from '@/components/layouts/Kpi/Table/dataTable'
import { commonUtil, currencyUtil } from '@/util'
import { NormalButton, WhiteButton } from '@/components/items'

export default {
  components: {
    LineChart,
    NormalButton,
    WhiteButton,
    DataTable
  },
  props: {
    aggregateList: {
      type: Array
    },
    supplierName: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isOrder: true,
      isLoading: true,
      selectedIds: [],
      labels: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月', ],
      aggregateTableData: [],
      aggregateChartData: [],
      aggregateRemarkData: [],
      colorSet: [
        'rgb(255, 0, 0)',
        'rgb(255, 125, 0)',
        'rgb(255, 225, 0)',
        'rgb(185, 225, 0)',
        'rgb(0, 225, 0)',
        'rgb(0, 225, 185)',
        'rgb(0, 125, 185)',
        'rgb(0, 0, 255)',
        'rgb(125, 0, 255)',
        'rgb(255, 0, 185)'
      ]
    }
  },
  async created() {
    // 親コンポーネントから渡された統計データをグラフ・表用に整形
    this.createChartData()
    await this.getData()
  },
  watch: {
    aggregateList: {
      deep: true,
      async handler(newVal, oldVal) {
        this.createChartData()
        await this.getData()
      }
    }
  },
  computed: {
    // 金額のグラフデータ整形
    chartData() {
      const datasets = []
      this.aggregateChartData.forEach((element, index) => {
        if (this.selectedIds.includes(element.productId)) {
          datasets.push({
            data: this.isOrder ? element.orderAmount : element.saleAmount,
            borderColor: this.colorSet[index % 10],
            borderWidth: 2.5,
            lineTension: 0,
            pointRadius: 0,
            fill: false
          })
        }
      })
      return {
        labels: this.labels,
        datasets: datasets
      }
    },
    // 数量のグラフデータ整形
    quantityChartData() {
      const datasets = []
      this.aggregateChartData.forEach((element, index) => {
        if (this.selectedIds.includes(element.productId)) {
          datasets.push({
            data: this.isOrder ? element.orderQuantity : element.saleQuantity,
            borderColor: this.colorSet[index % 10],
            borderWidth: 2.5,
            lineTension: 0,
            pointRadius: 0,
            fill: false
          })
        }
      })
      return {
        labels: this.labels,
        datasets: datasets
      }
    },
    options() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        scales: {
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: '金額'
              },
              gridLines: {
                drawBorder: false,
                color: '#efefef',
                zeroLineColor: '#efefef'
              },
              ticks: {
                beginAtZero: true,
                autoSkip: true,
                maxTicksLimit: 5,
                padding: 15,
                fontColor: '#aaaaaa',
                callback: function (label, index, labels) {
                  return commonUtil.formatPrice(Number(label)).toLocaleString()
                }
              }
            }
          ],
          xAxes: [
            {
              gridLines: {
                color: 'rgba(0, 0, 0, 0)'
              },
              ticks: {
                autoSkip: true,
                maxTicksLimit: 5,
                maxRotation: 0,
                minRotation: 0,
                fontColor: '#999999'
              }
            }
          ]
        }
      }
    },
    optionsForQuantity() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        scales: {
          yAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: '数量'
              },
              gridLines: {
                drawBorder: false,
                color: '#efefef',
                zeroLineColor: '#efefef'
              },
              ticks: {
                beginAtZero: true,
                autoSkip: true,
                maxTicksLimit: 5,
                padding: 15,
                fontColor: '#aaaaaa',
                callback: function (label, index, labels) {
                  return commonUtil.formatPrice(Number(label)).toLocaleString()
                }
              }
            }
          ],
          xAxes: [
            {
              gridLines: {
                color: 'rgba(0, 0, 0, 0)'
              },
              ticks: {
                autoSkip: true,
                maxTicksLimit: 5,
                maxRotation: 0,
                minRotation: 0,
                fontColor: '#999999'
              }
            }
          ]
        }
      }
    }
  },
  methods: {
    async changeChart(isOrderFlg) {
      this.isOrder = isOrderFlg ? true : false
    },
    priceFormat(amount) {
      const { currencyId } = 1
      const unit = currencyUtil.getCurrencyUnitName(currencyId)
      return isNaN(amount)
        ? ''
        : Math.floor(Number(amount)).toLocaleString() + unit
    },
    async getData() {
      this.isLoading = true
      // TODO データ取得処理
      this.isLoading = false
    },
    async changeData() {
      await this.getData()
    },
    setSelectedIds(selectedIds) {
      this.selectedIds = selectedIds
    },
    createChartData() {
      if (this.aggregateList.length > 0) {
        this.aggregateList.forEach((element, index) => {
          const orderAmount = []
          const orderQuantity = []
          const saleAmount = []
          const saleQuantity = []
          element.aggregateMonthOfYear.forEach((month, index) => {
            orderAmount.push(month.orderAmount)
            orderQuantity.push(month.orderQuantity)
            saleAmount.push(month.saleAmount)
            saleQuantity.push(month.saleQuantity)
          })
          // グラフ用データ
          this.aggregateChartData.push({
            productId: element.productId,
            productName: element.product.name_jp,
            orderAmount: orderAmount,
            orderQuantity: orderQuantity,
            saleAmount: saleAmount,
            saleQuantity: saleQuantity
          })
          // グラフ凡例用データ
          this.aggregateRemarkData.push({
            productId: element.productId,
            productName: element.product.nameJp
          })
          // 表のデータ
          this.aggregateTableData.push({
            productId: element.productId,
            supplierName: this.supplierName,
            productName: element.product.nameJp,
            amount1: element.aggregateMonthOfYear[0].orderQuantity,
            price1: this.priceFormat(element.aggregateMonthOfYear[0].orderAmount),
            amount2: element.aggregateMonthOfYear[1].orderQuantity,
            price2: this.priceFormat(element.aggregateMonthOfYear[1].orderAmount),
            amount3: element.aggregateMonthOfYear[2].orderQuantity,
            price3: this.priceFormat(element.aggregateMonthOfYear[2].orderAmount),
            amount4: element.aggregateMonthOfYear[3].orderQuantity,
            price4: this.priceFormat(element.aggregateMonthOfYear[3].orderAmount),
            amount5: element.aggregateMonthOfYear[4].orderQuantity,
            price5: this.priceFormat(element.aggregateMonthOfYear[4].orderAmount),
            amount6: element.aggregateMonthOfYear[5].orderQuantity,
            price6: this.priceFormat(element.aggregateMonthOfYear[5].orderAmount),
            amount7: element.aggregateMonthOfYear[6].orderQuantity,
            price7: this.priceFormat(element.aggregateMonthOfYear[6].orderAmount),
            amount8: element.aggregateMonthOfYear[7].orderQuantity,
            price8: this.priceFormat(element.aggregateMonthOfYear[7].orderAmount),
            amount9: element.aggregateMonthOfYear[8].orderQuantity,
            price9: this.priceFormat(element.aggregateMonthOfYear[8].orderAmount),
            amount10: element.aggregateMonthOfYear[9].orderQuantity,
            price10: this.priceFormat(element.aggregateMonthOfYear[9].orderAmount),
            amount11: element.aggregateMonthOfYear[10].orderQuantity,
            price11: this.priceFormat(element.aggregateMonthOfYear[10].orderAmount),
            amount12: element.aggregateMonthOfYear[11].orderQuantity,
            price12: this.priceFormat(element.aggregateMonthOfYear[11].orderAmount),
            amountTotal: element.totalOrderQuantity,
            priceTotal: this.priceFormat(element.totalOrderAmount),
            amount1forSale: element.aggregateMonthOfYear[0].saleQuantity,
            price1forSale: this.priceFormat(element.aggregateMonthOfYear[0].saleAmount),
            amount2forSale: element.aggregateMonthOfYear[1].saleQuantity,
            price2forSale: this.priceFormat(element.aggregateMonthOfYear[1].saleAmount),
            amount3forSale: element.aggregateMonthOfYear[2].saleQuantity,
            price3forSale: this.priceFormat(element.aggregateMonthOfYear[2].saleAmount),
            amount4forSale: element.aggregateMonthOfYear[3].saleQuantity,
            price4forSale: this.priceFormat(element.aggregateMonthOfYear[3].saleAmount),
            amount5forSale: element.aggregateMonthOfYear[4].saleQuantity,
            price5forSale: this.priceFormat(element.aggregateMonthOfYear[4].saleAmount),
            amount6forSale: element.aggregateMonthOfYear[5].saleQuantity,
            price6forSale: this.priceFormat(element.aggregateMonthOfYear[5].saleAmount),
            amount7forSale: element.aggregateMonthOfYear[6].saleQuantity,
            price7forSale: this.priceFormat(element.aggregateMonthOfYear[6].saleAmount),
            amount8forSale: element.aggregateMonthOfYear[7].saleQuantity,
            price8forSale: this.priceFormat(element.aggregateMonthOfYear[7].saleAmount),
            amount9forSale: element.aggregateMonthOfYear[8].saleQuantity,
            price9forSale: this.priceFormat(element.aggregateMonthOfYear[8].saleAmount),
            amount10forSale: element.aggregateMonthOfYear[9].saleQuantity,
            price10forSale: this.priceFormat(element.aggregateMonthOfYear[9].saleAmount),
            amount11forSale: element.aggregateMonthOfYear[10].saleQuantity,
            price11forSale: this.priceFormat(element.aggregateMonthOfYear[10].saleAmount),
            amount12forSale: element.aggregateMonthOfYear[11].saleQuantity,
            price12forSale: this.priceFormat(element.aggregateMonthOfYear[11].saleAmount),
            amountTotalforSale: element.totalSaleQuantity,
            priceTotalforSale: this.priceFormat(element.totalSaleAmount),
            isSelected: true
          })
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.chart-balance {
  min-width: 1126px;

}
.chartWrapper {
  display: flex;

  .chartCombo {
    position: relative;
    flex: 1;
  }

  .tableInfo {
    position: relative;
    flex: 10;
  }

  .chartInfo {
    position: relative;
    flex: 1;
    height: 300px;
    padding-left: 40px;
    padding-right: 40px;
    padding-top: 40px;
    .loading {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
  .dataInfo {
    width: 260px;
    display: flex;
    flex-direction: column;
    padding-top: 20px;
    .periodSelect {
      color: #999999;
      .arrowIcon {
        &.isOpen {
          transform: rotate(180deg);
        }
      }
    }
    .remarks {
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      margin-top: 5px;
      margin-bottom: 5px;
      .label {
        position: relative;
        color: #666666;
        &:before {
          content: '';
          width: 14px;
          height: 14px;
          border-radius: 14px;
          position: absolute;
          left: -22px;
          top: 2px;
        }
      }
      .color1 {
        &:before {
          background: #FF0000;
        }
      }

      .color2 {
        &:before {
          background: #FF7D00;
        }
      }

      .color3 {
        &:before {
          background: #FFE100;
        }
      }

      .color4 {
        &:before {
          background: #B9E100;
        }
      }

      .color5 {
        &:before {
          background: #00E100;
        }
      }

      .color6 {
        &:before {
          background: #00E1B9;
        }
      }

      .color7 {
        &:before {
          background: #007DB9;
        }
      }

      .color8 {
        &:before {
          background: #0000E1;
        }
      }

      .color9 {
        &:before {
          background: #7D00E1;
        }
      }

      .color0 {
        &:before {
          background: #FF00B9;
        }
      }
    }
  }
}
</style>
